import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2, Mail, CheckCircle2, Sparkles, MessageCircle, Send } from "lucide-react";
import { API } from "@/lib/auth";
import { Footer } from "@/components/Footer";
import ReactMarkdown from "react-markdown";
import { mdComponents } from "@/components/mdComponents";
import { CARD_BACK, shuffledDeck, ARCANE_BY_ID } from "@/data/tarot";
import { ShareReading } from "@/components/ShareReading";
import { usePageMeta } from "@/lib/seo";

const C = {
  bg: "#1C1C1E",
  title: "#F4EAD4",
  body: "#E5E5EA",
  mist: "#A2A2A6",
  goldMat: "#C5A059",
  goldLine: "#E6C687",
};

const CROSS_CELL = {
  Résultat: "col-start-2 row-start-1",
  Pour: "col-start-1 row-start-2",
  "Situation actuelle": "col-start-2 row-start-2",
  Contre: "col-start-3 row-start-2",
  Synthèse: "col-start-2 row-start-3",
};


// Animation "mélange des énergies" — vrai battage de cartes
const ShuffleLoader = ({ label }) => {
  const cards = [0, 1, 2, 3, 4, 5, 6];
  return (
    <div className="flex flex-col items-center justify-center min-h-[62vh]" data-testid="shuffle-animation">
      <div className="relative w-72 h-64 flex items-center justify-center" style={{ perspective: 1000 }}>
        {[0, 1].map((i) => (
          <motion.span
            key={`halo-${i}`}
            className="absolute rounded-full border"
            style={{ width: 90, height: 90, borderColor: "rgba(230,198,135,0.35)" }}
            animate={{ scale: [1, 3.6], opacity: [0.4, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeOut", delay: i * 1.5 }}
          />
        ))}
        {cards.map((k) => {
          const side = k % 2 === 0 ? 1 : -1;
          return (
            <motion.div
              key={k}
              className="absolute rounded-lg border overflow-hidden"
              style={{ width: 82, height: 124, borderColor: "rgba(230,198,135,0.5)", background: "#232326", zIndex: k }}
              animate={{
                x: [0, side * (60 + k * 8), side * 12, 0],
                y: [0, -k * 3, k * 2, 0],
                rotateZ: [0, side * 14, side * -6, 0],
                rotateY: [0, 24, 0],
              }}
              transition={{
                duration: 2.4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: k * 0.16,
              }}
            >
              <img src={CARD_BACK} alt="" className="w-full h-full object-cover opacity-95" draggable={false} />
            </motion.div>
          );
        })}
      </div>
      <motion.p
        animate={{ opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="mt-10 font-display uppercase tracking-[0.18em] text-lg sm:text-xl text-center"
        style={{ color: C.body }}
      >
        {label}
      </motion.p>
    </div>
  );
};

const EmailCapture = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return toast.error("Entre une adresse email valide.");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/club/subscribe`, { email: value });
      setDone(
        data.already
          ? "Tu es déjà inscrite dans notre club privé et ta place prioritaire est bien réservée. Reste connectée !"
          : "Merci. Ton inscription est validée. Tu es désormais prioritaire : tu recevras un message dès qu'une nouveauté ou qu'une place en privé se libère. Reste connectée."
      );
    } catch (err) {
      toast.error("Inscription impossible. Réessaye.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="mt-10 w-full max-w-lg rounded-2xl border p-6 sm:p-8" style={{ borderColor: "rgba(229,229,234,0.10)", backgroundColor: "rgba(255,255,255,0.025)" }}>
      {done ? (
        <motion.div data-testid="club-thankyou" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center text-center gap-3">
          <CheckCircle2 size={32} strokeWidth={1.4} style={{ color: C.goldLine }} />
          <p className="font-body text-base leading-relaxed" style={{ color: C.body }}>{done}</p>
        </motion.div>
      ) : (
        <>
          <p className="font-body text-sm leading-relaxed text-center mb-5" style={{ color: C.mist }}>
            Prochainement : ouverture des consultations de voyance privées avec Lucie et lancement de nouvelles
            applications exclusives. Laisse ton adresse email pour être informée en avant-première et prévenue en
            priorité dès l'ouverture :
          </p>
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
            <input data-testid="club-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ton@email.com" className="flex-1 bg-transparent border rounded-md px-4 py-3 font-body text-sm focus:outline-none" style={{ borderColor: "rgba(229,229,234,0.25)", color: C.body }} />
            <button data-testid="club-subscribe-button" type="submit" disabled={loading} style={{ backgroundColor: C.goldMat, color: C.bg }} className="flex items-center justify-center gap-2 tracking-[0.12em] uppercase rounded-md px-6 py-3 font-body text-sm transition-opacity hover:opacity-90 disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />}
              M'inscrire
            </button>
          </form>
        </>
      )}
    </div>
  );
};

const CONSEIL_WELCOME =
  "Je suis ton conseiller intuitif du cœur. Je suis là, juste après ton tirage. Dis-moi ce qui te préoccupe, ce que tu ressens... et on regarde ça ensemble.";

const ConseilChat = ({ reading }) => {
  const MAX = 8;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const scrollRef = useRef(null);

  const userCount = messages.filter((m) => m.role === "user").length;
  const ctaVisible = limitReached || userCount >= MAX;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, ctaVisible, open]);

  const context = {
    question: reading.question,
    prenom: reading.prenom,
    prenom_autre: reading.prenom_autre,
    cards: (reading.cards || []).map((c) => ({ name: c.name, position: c.position, numeral: c.numeral || "" })),
  };

  const send = async (e) => {
    e.preventDefault();
    const value = input.trim();
    if (!value || sending || ctaVisible) return;
    const next = [...messages, { role: "user", content: value }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { context, messages: next });
      if (data.limit_reached) setLimitReached(true);
      else if (data.reply) setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      toast.error("Le conseiller est indisponible un instant. Réessaie.");
    } finally {
      setSending(false);
    }
  };

  const Bubble = ({ role, children }) => (
    <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}>
      <div
        className="max-w-[85%] rounded-2xl px-4 py-3 font-body text-[14px] leading-relaxed whitespace-pre-wrap"
        style={
          role === "user"
            ? { backgroundColor: "rgba(197,160,89,0.18)", color: C.title, borderTopRightRadius: 4 }
            : { backgroundColor: "rgba(255,255,255,0.04)", color: C.body, border: "1px solid rgba(230,198,135,0.14)", borderTopLeftRadius: 4 }
        }
      >
        {children}
      </div>
    </div>
  );

  return (
    <div className="mt-12 w-full max-w-xl" data-testid="conseil-section">
      <span className="block mx-auto mb-8 h-px w-16" style={{ backgroundColor: "rgba(230,198,135,0.35)" }} />
      {!open ? (
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <MessageCircle size={26} strokeWidth={1.3} style={{ color: C.goldLine }} />
          </div>
          <p className="font-body text-sm leading-relaxed mb-5" style={{ color: C.mist }}>
            Ton tirage te laisse une question en suspens, ou tu as besoin d'en parler ? Échange en toute confidentialité
            avec ton conseiller intuitif, en attendant l'ouverture des consultations de voyance privées.
          </p>
          <button
            data-testid="conseil-open-button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 tracking-[0.12em] uppercase rounded-full px-7 py-3 font-body text-sm transition-opacity hover:opacity-90"
            style={{ border: `1px solid ${C.goldMat}`, color: C.goldLine }}
          >
            <Sparkles size={16} />
            Échanger avec ton conseiller intuitif
          </button>
        </div>
      ) : (
        <div
          data-testid="conseil-chat"
          className="rounded-2xl border overflow-hidden"
          style={{ borderColor: "rgba(230,198,135,0.18)", backgroundColor: "rgba(255,255,255,0.02)" }}
        >
          <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: "rgba(230,198,135,0.12)" }}>
            <MessageCircle size={20} strokeWidth={1.4} style={{ color: C.goldLine }} />
            <div>
              <p className="font-display uppercase tracking-[0.14em] text-sm" style={{ color: C.title }}>
                Ton conseiller intuitif
              </p>
              <p className="font-body text-[11px]" style={{ color: C.mist }}>Conseil du cœur · rien n'est enregistré</p>
            </div>
          </div>

          <div ref={scrollRef} className="px-5 py-5 space-y-4 max-h-[440px] overflow-y-auto">
            <Bubble role="assistant">{CONSEIL_WELCOME}</Bubble>
            {messages.map((m, i) => (
              <div key={i} data-testid={`conseil-message-${i}`}>
                <Bubble role={m.role}>
                  {m.role === "assistant" ? m.content.replace(/\*\*(.*?)\*\*/g, "$1").replace(/\*(.*?)\*/g, "$1") : m.content}
                </Bubble>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="conseil-typing">
                <div className="rounded-2xl px-4 py-3" style={{ backgroundColor: "rgba(255,255,255,0.04)", border: "1px solid rgba(230,198,135,0.14)" }}>
                  <Loader2 className="animate-spin" size={16} style={{ color: C.goldLine }} />
                </div>
              </div>
            )}

            {ctaVisible && (
              <div className="mt-4 rounded-xl border p-5 text-center" data-testid="conseil-cta" style={{ borderColor: "rgba(230,198,135,0.2)", backgroundColor: "rgba(197,160,89,0.06)" }}>
                <p className="font-body text-sm leading-relaxed" style={{ color: C.body }}>
                  On a bien échangé pour cette fois. Pour aller plus loin, une vraie consultation de voyance en privé,
                  avec Lucie, ouvrira bientôt. Laisse ton email juste en dessous pour être prévenue en priorité.
                </p>
              </div>
            )}
          </div>

          {!ctaVisible && (
            <form onSubmit={send} className="flex items-center gap-2 px-4 py-3 border-t" style={{ borderColor: "rgba(230,198,135,0.12)" }}>
              <input
                data-testid="conseil-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Écris ton message..."
                disabled={sending}
                className="flex-1 bg-transparent border rounded-full px-4 py-2.5 font-body text-sm focus:outline-none disabled:opacity-60"
                style={{ borderColor: "rgba(229,229,234,0.25)", color: C.body }}
              />
              <button
                data-testid="conseil-send-button"
                type="submit"
                disabled={sending || !input.trim()}
                className="flex items-center justify-center rounded-full h-10 w-10 shrink-0 transition-opacity hover:opacity-90 disabled:opacity-40"
                style={{ backgroundColor: C.goldMat, color: C.bg }}
              >
                <Send size={16} />
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};


export default function Oracle() {
  usePageMeta({
    title: "Tirage en Croix gratuit · Tarot de Marseille | La Petite Boîte Intuitive",
    description: "Pose ta question du cœur et tire 5 lames du Tarot de Marseille. Une interprétation claire, honnête et sans complaisance, gratuite.",
    path: "/tirage",
  });
  // home | waiting | verifying | shuffle | tapis | revealing | reveal
  const [step, setStep] = useState("home");
  const [prenom, setPrenom] = useState("");
  const [prenomAutre, setPrenomAutre] = useState("");
  const [question, setQuestion] = useState("");
  const [price, setPrice] = useState({ amount: 5, currency: "eur", free_launch: true });
  const [submitting, setSubmitting] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [deck, setDeck] = useState([]);
  const [picked, setPicked] = useState([]); // arcana ids in pick order
  const [reading, setReading] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const timer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/price`).then(({ data }) => setPrice(data)).catch(() => {});
    return () => clearTimeout(timer.current);
  }, []);

  const priceLabel = `${Number(price.amount).toFixed(0)} €`;

  const enterTapis = useCallback((meta) => {
    if (meta) {
      if (meta.prenom) setPrenom((p) => p || meta.prenom);
      if (meta.prenom_autre) setPrenomAutre((p) => p || meta.prenom_autre);
      if (meta.question) setQuestion((q) => q || meta.question);
    }
    setStep("shuffle");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setDeck(shuffledDeck());
      setPicked([]);
      setStep("tapis");
    }, 6500);
  }, []);

  const pollStatus = useCallback(
    async (sid, attempts = 0) => {
      if (attempts >= 100) {
        toast.error("Paiement non détecté. Si tu as payé, recharge la page.");
        setStep("home");
        return;
      }
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sid}`);
        if (data.paid) {
          setSessionId(sid);
          if (data.drawn && data.reading) {
            setReading(data.reading);
            setStep("reveal");
          } else {
            enterTapis(data);
          }
          return;
        }
        if (data.status === "expired") {
          toast.error("La session de paiement a expiré. Réessaye.");
          setStep("home");
          return;
        }
      } catch (e) {
        // erreur transitoire : continuer à sonder
      }
      clearTimeout(timer.current);
      timer.current = setTimeout(() => pollStatus(sid, attempts + 1), 3000);
    },
    [enterTapis]
  );

  useEffect(() => {
    const sid = searchParams.get("session_id");
    if (sid) {
      setStep("verifying");
      setSearchParams({}, { replace: true });
      pollStatus(sid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openBox = async () => {
    if (submitting) return;
    if (!prenom.trim()) return toast.error("Indique ton prénom.");
    if (!prenomAutre.trim()) return toast.error("Indique le prénom de la personne concernée.");
    if (!question.trim()) return toast.error("Pose ta question.");
    setSubmitting(true);

    // Offre de lancement gratuite : on court-circuite Stripe et on ouvre directement le tapis.
    if (price.free_launch) {
      try {
        const { data } = await axios.post(`${API}/readings/free-start`, {
          prenom: prenom.trim(),
          prenom_autre: prenomAutre.trim(),
          question: question.trim(),
          origin_url: window.location.origin + "/tirage",
        });
        setSessionId(data.session_id);
        setSubmitting(false);
        enterTapis();
      } catch (e) {
        setSubmitting(false);
        toast.error("Impossible d'ouvrir la Petite Boîte. Réessaye.");
      }
      return;
    }

    try {
      const { data } = await axios.post(`${API}/checkout`, {
        prenom: prenom.trim(),
        prenom_autre: prenomAutre.trim(),
        question: question.trim(),
        origin_url: window.location.origin + "/tirage",
      });
      setCheckoutUrl(data.url);
      setSessionId(data.session_id);
      window.open(data.url, "_blank");
      setSubmitting(false);
      setStep("waiting");
      pollStatus(data.session_id);
    } catch (e) {
      setSubmitting(false);
      toast.error("Impossible d'initier le paiement. Réessaye.");
    }
  };

  const toggleCard = (arcaneId) => {
    setPicked((prev) => {
      if (prev.includes(arcaneId)) return prev.filter((x) => x !== arcaneId);
      if (prev.length >= 5) return prev;
      return [...prev, arcaneId];
    });
  };

  const revealCards = async () => {
    if (picked.length !== 5 || !sessionId) return;
    setStep("revealing");
    try {
      const { data } = await axios.post(`${API}/readings/${sessionId}/draw`, {
        cards: picked,
        prenom: prenom.trim(),
        prenom_autre: prenomAutre.trim(),
        question: question.trim(),
      });
      setReading(data);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setStep("reveal"), 700);
    } catch (e) {
      toast.error("La lecture n'a pas pu être générée. Réessaye.");
      setStep("tapis");
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col" style={{ backgroundColor: C.bg }}>
      <div className="oracle-bg" />
      <div className="relative z-10 flex-1 max-w-5xl w-full mx-auto px-6 py-12 sm:py-16">
        <AnimatePresence mode="wait">
          {/* ===================== ACCUEIL : INTENTION ===================== */}
          {step === "home" && (
            <motion.div key="home" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.5 }} className="max-w-2xl mx-auto">
              <div className="text-center mb-10">
                <p className="font-body uppercase tracking-[0.35em] text-xs sm:text-sm" style={{ color: C.mist }}>Tarot de Marseille · Tirage en Croix</p>
                <div className="flex justify-center mt-4 mb-5">
                  <span data-testid="offer-mention" className="inline-block font-body text-xs tracking-wide rounded-full px-3.5 py-1 border" style={{ color: C.goldLine, borderColor: "rgba(230,198,135,0.4)", backgroundColor: "rgba(230,198,135,0.06)" }}>
                    {price.free_launch ? "✨ Lancement · Consultation offerte" : `✨ Consultation : ${priceLabel}`}
                  </span>
                </div>
                <h1 className="font-display uppercase tracking-[0.06em] text-4xl sm:text-5xl leading-tight font-light" style={{ color: C.title }}>La Petite Boîte Intuitive</h1>
                <div className="flex items-center justify-center gap-3 mt-4">
                  <span className="h-px w-8" style={{ backgroundColor: "rgba(230,198,135,0.4)" }} />
                  <p data-testid="reading-nature" className="font-body italic text-sm sm:text-base" style={{ color: C.goldLine }}>Tirage d'ordre sentimental · les questions de cœur</p>
                  <span className="h-px w-8" style={{ backgroundColor: "rgba(230,198,135,0.4)" }} />
                </div>
                <p className="font-body text-[11px] tracking-[0.14em] mt-3" style={{ color: C.mist, fontWeight: 300 }}>Propulsé par L'Observatoire des Possibles</p>
              </div>

              <div className="space-y-8 rounded-2xl p-8 sm:p-10 border" style={{ borderColor: "rgba(229,229,234,0.10)", backgroundColor: "rgba(255,255,255,0.025)" }}>
                <div>
                  <label className="block text-center font-body text-sm mb-3" style={{ color: C.mist }}>Ton prénom</label>
                  <input data-testid="prenom-input" value={prenom} onChange={(e) => setPrenom(e.target.value)} placeholder="Prénom" className="oracle-input font-display text-2xl w-full py-2" />
                </div>
                <div>
                  <label className="block text-center font-body text-sm mb-3" style={{ color: C.mist }}>Prénom de la personne concernée</label>
                  <input data-testid="prenom-autre-input" value={prenomAutre} onChange={(e) => setPrenomAutre(e.target.value)} placeholder="Son prénom" className="oracle-input font-display text-xl w-full py-2" />
                </div>
                <div>
                  <label className="block text-center font-body text-sm mb-3" style={{ color: C.mist }}>Ta question</label>
                  <input data-testid="question-input" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ex : Va-t-il revenir ? Que ressent-il pour moi ?..." className="oracle-input font-display text-lg w-full py-2" />
                </div>
              </div>

              <div className="text-center mt-10">
                <button data-testid="open-box-button" onClick={openBox} disabled={submitting} style={{ backgroundColor: C.goldMat, color: C.bg }} className="inline-flex items-center gap-2 tracking-[0.12em] uppercase rounded-sm px-8 py-4 font-body text-sm transition-opacity hover:opacity-90 disabled:opacity-60">
                  {submitting ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
                  Ouvrir la petite boîte
                </button>
                <p data-testid="confidentialite-note" className="flex items-center justify-center gap-2 font-body text-xs mt-6 max-w-md mx-auto leading-relaxed" style={{ color: C.mist }}>
                  <Lock size={13} strokeWidth={1.6} style={{ color: C.goldLine, flexShrink: 0 }} />
                  Confidentialité totale : aucune donnée n'est conservée. Ni ton prénom, ni ta question, ni tes cartes.
                  Ton tirage reste vierge et intuitif à chaque fois.
                </p>
              </div>
            </motion.div>
          )}

          {/* ===================== ATTENTE PAIEMENT ===================== */}
          {step === "waiting" && (
            <motion.div key="waiting" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center text-center min-h-[55vh]">
              <Loader2 className="animate-spin mb-6" size={40} strokeWidth={1.5} style={{ color: C.goldMat }} />
              <p className="font-display uppercase tracking-[0.15em] text-lg sm:text-xl" style={{ color: C.body }}>Finalise ton paiement sécurisé ({priceLabel})</p>
              <p className="font-body text-sm mt-4 max-w-md leading-relaxed" style={{ color: C.mist }}>Une page Stripe s'est ouverte dans un nouvel onglet. Dès que ton paiement est validé, la Petite Boîte s'ouvre ici automatiquement.</p>
              <div className="flex flex-col sm:flex-row items-center gap-3 mt-8">
                <button data-testid="reopen-payment-button" onClick={() => checkoutUrl && window.open(checkoutUrl, "_blank", "noopener,noreferrer")} style={{ backgroundColor: C.goldMat, color: C.bg }} className="tracking-[0.12em] uppercase rounded-sm px-6 py-3 font-body text-sm transition-opacity hover:opacity-90">Rouvrir la page de paiement</button>
                <button data-testid="cancel-payment-button" onClick={() => { clearTimeout(timer.current); setStep("home"); }} className="font-body text-sm" style={{ color: C.mist }}>Annuler</button>
              </div>
            </motion.div>
          )}

          {/* ===================== VÉRIFICATION ===================== */}
          {step === "verifying" && (
            <motion.div key="verifying" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center min-h-[55vh]">
              <Loader2 className="animate-spin" size={40} strokeWidth={1.5} style={{ color: C.goldMat }} />
              <p className="mt-6 font-display uppercase tracking-[0.15em] text-lg" style={{ color: C.body }}>Vérification du paiement...</p>
            </motion.div>
          )}

          {/* ===================== ANIMATION MÉLANGE ===================== */}
          {step === "shuffle" && (
            <motion.div key="shuffle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <ShuffleLoader label="Les énergies se mélangent..." />
            </motion.div>
          )}

          {/* ===================== LE TAPIS : CHOIX DES 5 CARTES ===================== */}
          {step === "tapis" && (
            <motion.div key="tapis" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center">
              <h2 className="font-display uppercase tracking-[0.12em] text-2xl sm:text-3xl text-center font-light" style={{ color: C.title }}>Choisis tes 5 cartes</h2>
              <p className="font-body text-sm mt-3 text-center max-w-md leading-relaxed" style={{ color: C.mist }}>
                Laisse ton intuition te guider. Elles formeront ta Croix : Pour, Contre, Situation actuelle, Résultat, Synthèse.
              </p>
              <p data-testid="picked-count" className="font-body text-sm mt-4 tracking-[0.12em]" style={{ color: C.goldLine }}>{picked.length} / 5 choisies</p>

              <div data-testid="tapis-grid" className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2.5 sm:gap-3 mt-8 w-full">
                {deck.map((card, i) => {
                  const isPicked = picked.includes(card.id);
                  const order = picked.indexOf(card.id) + 1;
                  const disabled = !isPicked && picked.length >= 5;
                  return (
                    <motion.button
                      key={card.id}
                      data-testid={`tapis-card-${i}`}
                      onClick={() => toggleCard(card.id)}
                      disabled={disabled}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: isPicked ? -8 : 0 }}
                      transition={{ delay: i * 0.02 }}
                      whileHover={{ y: -6 }}
                      className="relative aspect-[2/3] rounded-lg overflow-hidden border transition-all"
                      style={{
                        borderColor: isPicked ? C.goldLine : "rgba(230,198,135,0.25)",
                        boxShadow: isPicked ? "0 0 18px rgba(230,198,135,0.5)" : "none",
                        opacity: disabled ? 0.4 : 1,
                        cursor: disabled ? "not-allowed" : "pointer",
                      }}
                    >
                      <img src={CARD_BACK} alt="Carte face cachée" className="w-full h-full object-cover" draggable={false} />
                      <div className="absolute bottom-[7%] inset-x-0 flex items-center justify-center pointer-events-none px-1">
                        <span className="font-display uppercase text-center whitespace-nowrap" style={{ color: C.goldLine, fontSize: "5.5px", letterSpacing: "0.06em", opacity: 0.55, textShadow: "0 1px 3px rgba(0,0,0,0.9)" }}>
                          La Petite Boîte Intuitive
                        </span>
                      </div>
                      {isPicked && (
                        <span className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center font-body text-[11px]" style={{ backgroundColor: C.goldMat, color: C.bg }}>{order}</span>
                      )}
                    </motion.button>
                  );
                })}
              </div>

              <button
                data-testid="reveal-button"
                onClick={revealCards}
                disabled={picked.length !== 5}
                style={{ backgroundColor: picked.length === 5 ? C.goldMat : "transparent", color: picked.length === 5 ? C.bg : C.mist, borderColor: "rgba(230,198,135,0.35)" }}
                className="mt-10 inline-flex items-center gap-2 tracking-[0.12em] uppercase rounded-sm px-8 py-4 font-body text-sm border transition-all disabled:opacity-60"
              >
                Révéler les cartes
              </button>
            </motion.div>
          )}

          {/* ===================== ANIMATION AVANT RÉVÉLATION ===================== */}
          {step === "revealing" && (
            <motion.div key="revealing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <ShuffleLoader label="Les arcanes se retournent..." />
            </motion.div>
          )}

          {/* ===================== RÉVÉLATION + INTERPRÉTATION ===================== */}
          {step === "reveal" && reading && (
            <motion.div key="reveal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center">
              <div data-testid="reveal-header" className="w-full max-w-xl mb-10 text-center">
                <p className="font-display text-2xl sm:text-3xl font-light leading-snug" style={{ color: C.title }}>
                  <span data-testid="reveal-prenom">{reading.prenom}</span>
                  <span style={{ color: C.mist }}>, au sujet de </span>
                  {reading.prenom_autre}
                </p>
                <p className="font-body italic text-base sm:text-lg leading-relaxed mt-4" style={{ color: C.goldLine }}>
                  « {reading.question} »
                </p>
                <span className="block mx-auto mt-7 h-px w-16" style={{ backgroundColor: "rgba(230,198,135,0.4)" }} />
              </div>

              {/* La Croix */}
              <div data-testid="cross-layout" className="grid grid-cols-3 grid-rows-3 gap-3 sm:gap-4 mb-10">
                {(reading.cards || []).map((card, idx) => (
                  <motion.div
                    key={card.id}
                    data-testid={`cross-card-${card.position}`}
                    initial={{ rotateY: 180, opacity: 0 }}
                    animate={{ rotateY: 0, opacity: 1 }}
                    transition={{ delay: idx * 0.25, duration: 0.6 }}
                    className={`${CROSS_CELL[card.position] || ""} flex flex-col items-center`}
                    style={{ transformStyle: "preserve-3d" }}
                  >
                    <div className="relative w-20 sm:w-28 aspect-[2/3] rounded-lg overflow-hidden border" style={{ borderColor: "rgba(230,198,135,0.45)", boxShadow: "0 0 16px rgba(230,198,135,0.3)" }}>
                      <img src={ARCANE_BY_ID[card.id].image} alt={card.name} className="w-full h-full object-cover" />
                      <div className="absolute top-0 inset-x-0 h-[26%] pointer-events-none" style={{ background: "linear-gradient(to bottom, rgba(20,20,22,0.72), transparent)" }} />
                      <div className="absolute bottom-0 inset-x-0 h-[30%] pointer-events-none" style={{ background: "linear-gradient(to top, rgba(20,20,22,0.82), transparent)" }} />
                      <span className="absolute top-[3px] sm:top-1 inset-x-0 text-center font-display tracking-[0.18em] pointer-events-none" style={{ color: C.goldLine, fontSize: "10px", textShadow: "0 1px 4px rgba(0,0,0,0.95)" }}>{ARCANE_BY_ID[card.id].numeral}</span>
                      <span className="absolute bottom-[3px] sm:bottom-1 inset-x-0 px-0.5 text-center font-display uppercase leading-[1.05] tracking-[0.04em] pointer-events-none" style={{ color: C.goldLine, fontSize: "8.5px", textShadow: "0 1px 4px rgba(0,0,0,0.95)" }}>{card.name}</span>
                    </div>
                    <span className="mt-2 font-body text-[10px] sm:text-[11px] uppercase tracking-[0.1em] text-center" style={{ color: C.goldLine }}>{card.position}</span>
                  </motion.div>
                ))}
              </div>

              <motion.div data-testid="interpretation-text" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }} className="w-full max-w-xl text-left">
                <ReactMarkdown components={mdComponents}>{reading.interpretation}</ReactMarkdown>
              </motion.div>

              <ShareReading reading={reading} />

              <div data-testid="free-will-note" className="w-full max-w-xl mt-10">
                <span className="block mx-auto mb-6 h-px w-16" style={{ backgroundColor: "rgba(230,198,135,0.35)" }} />
                <p className="font-body italic text-sm leading-relaxed text-center" style={{ color: C.mist }}>
                  Souviens-toi : le Tarot éclaire une tendance, une énergie du moment — jamais une fatalité.
                  Rien n'est figé, et c'est toujours toi qui gardes la main sur ta vie. Ces cartes sont une
                  invitation à ressentir et à te connaître, pas une vérité gravée : ton libre arbitre reste
                  ton plus grand pouvoir.
                </p>
              </div>

              <ConseilChat reading={reading} />

              <EmailCapture />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Footer />

      {step === "home" && (
        <button data-testid="admin-link" onClick={() => navigate("/admin")} className="fixed bottom-4 right-4 z-20" style={{ color: "rgba(162,162,166,0.4)" }} aria-label="Espace administrateur">
          <Lock size={16} strokeWidth={1.4} />
        </button>
      )}
    </div>
  );
}
